import OpenAI from 'openai';
import { getApiConfig } from './api-config';
import { logApiRequest } from './events';
import { AI_CONFIG } from '../config/ai';

interface AIExplanationResponse {
  explanation: string;
  concept: string;
}

export async function getAIExplanation(
  question: string, 
  options: { [key: string]: string },
  selectedAnswer: string
): Promise<AIExplanationResponse> {
  const config = getApiConfig();
  
  if (!config.apiKey) {
    throw new Error('API key is missing');
  }

  const openai = new OpenAI({
    apiKey: config.apiKey,
    dangerouslyAllowBrowser: true
  });

  const optionsText = Object.entries(options)
    .map(([key, value]) => `${key}. ${value}`)
    .join('\n');

  const prompt = `Analyze this multiple choice question and provide:

1. A comprehensive explanation that:
   - Primarily focuses on explaining why the selected answer (${selectedAnswer}) is correct
   - Only disagree with the selected answer if you are absolutely certain it's incorrect based on well-established medical/scientific facts
   - If you're not completely certain the selected answer is wrong, explain it as if it's correct
   - Provide scientific reasoning and evidence to support the explanation
   - Briefly address why other plausible options are incorrect (skip obviously wrong options)
   - Include relevant clinical applications or practical significance
   - Be concise and focused

2. Extract the key scientific concept in a clear, objective format suitable for flashcards. Focus on:
   - Core principle or definition being tested
   - Key relationships or mechanisms
   - Important clinical/practical implications
   Make it direct, factual, and memorable - no explanatory language.

Question:
${question}

Options:
${optionsText}

Selected Answer: ${selectedAnswer}

Format your response as JSON with two fields:
{
  "explanation": "your explanation here",
  "concept": "key scientific concept in direct, objective format"
}`;

  try {
    const request = {
      model: AI_CONFIG.openai.model,
      messages: [
        {
          role: "system",
          content: "You are a precise medical educator. Be conservative in disagreeing with provided answers - only contradict if you are absolutely certain based on well-established medical facts. For explanations, be clear and supportive. For concepts, be direct and objective - perfect for flashcards and study notes."
        },
        {
          role: "user",
          content: prompt
        }
      ],
      ...AI_CONFIG.openai.settings
    };

    logApiRequest({
      provider: 'openai',
      request: {
        ...request,
        question,
        options,
        selectedAnswer
      }
    });

    const response = await openai.chat.completions.create(request);
    const content = response.choices[0].message.content || '{}';
    
    let parsedResponse: AIExplanationResponse;
    try {
      parsedResponse = JSON.parse(content);
    } catch (e) {
      parsedResponse = {
        explanation: content,
        concept: 'Failed to extract concept'
      };
    }

    logApiRequest({
      provider: 'openai',
      request: {
        ...request,
        question,
        options,
        selectedAnswer
      },
      response: {
        parsedResponse,
        usage: response.usage
      }
    });

    return parsedResponse;
  } catch (error) {
    logApiRequest({
      provider: 'openai',
      request: { question, options, selectedAnswer },
      error: error instanceof Error ? error.message : 'Unknown error'
    });
    throw new Error('Failed to get AI explanation');
  }
}